<template>
  <vs-wrapper>
    <v-skeleton-loader v-if="isLoading" type="image" rounded />
    <VsTimeline v-if="!isLoading" :last-item="lastElementOfVsTimeline" class="mb-4">
      <VsTimelineItem
        v-for="(stop, index) in sortedActualStops"
        :ref="(el) => actualStopRefCallback(el, index)"
        :key="'actual-stop-' + stop.id + index"
        :icon="getTimelineIcon(stop.stopType)"
      >
        <JobActualStopDisplay
          :stop="stop"
          :job-number="jobNumber"
          :requested-stop="getRequestedStop(stop)"
        />
      </VsTimelineItem>

      <VsTimelineItem
        v-for="(stop, index) in futureStops"
        :ref="(el) => requestedStopRefCallback(el, index)"
        :key="'requested-stop-' + stop.locationId + '-' + index"
        :icon="getTimelineIcon(stop.stopType)"
      >
        <JobRequestedStopDisplay
          :stop="stop"
          :job-number="jobNumber"
          :is-pickup="stop.stopType === JobStopType.PickUp"
          :show-edit-button="showEditButton && futureStops.length == index + 1"
        />
      </VsTimelineItem>
    </VsTimeline>
  </vs-wrapper>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { useGetJobRequestedStopsQuery, useGetJobStopsQuery } from '@/api/jobs'
import { ActualStop, JobStopType } from '@ironsight/types'
import JobRequestedStopDisplay from '@/components/tickets/StopsAndInventories/JobRequestedStopDisplay.vue'
import JobActualStopDisplay from '@/components/tickets/StopsAndInventories/JobActualStopDisplay.vue'
import { orderBy, sortBy } from 'lodash'
import VsTimeline from '@/components/vision/VsTimeline.vue'
import VsTimelineItem from '@/components/vision/VsTimelineItem.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'

interface Props {
  jobId: string
  jobNumber: number
  showEditButton?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showEditButton: false,
})

const jobIdRef = toRef(props, 'jobId')

const { data: requestedStops, isLoading: isLoadingRequestedStops } =
  useGetJobRequestedStopsQuery(jobIdRef)
const { data: actualStops, isLoading: isLoadingActualStops } = useGetJobStopsQuery(jobIdRef)

const isLoading = computed(() => isLoadingRequestedStops.value || isLoadingActualStops.value)

const getTimelineIcon = (stopType: JobStopType): string =>
  stopType === JobStopType.PickUp ? 'mdi-map-marker-outline' : 'mdi-flag-outline'

const getRequestedStop = (stop: ActualStop) =>
  requestedStops.value?.find((rs) => rs.id === stop.requestedStopId) ?? null

const sortedActualStops = computed(() => sortBy(actualStops.value, ['arrivedAt', 'occurredAt']))
const sortedRequestedStops = computed(() =>
  orderBy(requestedStops.value, ['position', 'stopType'], ['asc', 'desc']),
)
const futureStops = computed(
  () =>
    sortedRequestedStops.value?.filter(
      (stop) => !sortedActualStops.value.map((s) => s.requestedStopId).includes(stop.id),
    ) ?? [],
)

// The following logic is for UI purposes only.
// These logics are to remove the "end-line" of v-timeline
// See the illustration of "start-line" and "end-line" here of <vs-timeline> component:
// https://stackoverflow.com/questions/65227875/remove-start-line-and-end-line-of-timeline-v-timeline-vuetify

const lastActualStopRef = ref()
watch(sortedActualStops, () => {
  lastActualStopRef.value = undefined
})
const actualStopRefCallback = (el, i) => {
  if (el && i === sortedActualStops.value.length - 1) {
    lastActualStopRef.value = el
  }
}

const lastRequestedStopRef = ref()
watch(futureStops, () => {
  lastRequestedStopRef.value = undefined
})
const requestedStopRefCallback = (el, i) => {
  if (el && i === futureStops.value.length - 1) {
    lastRequestedStopRef.value = el
  }
}

const lastElementOfVsTimeline = computed(
  () => lastRequestedStopRef.value ?? lastActualStopRef.value,
)
</script>
